/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalCredentialsVerifyGcpIam } from "../funcs/externalCredentialsVerifyGcpIam.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { VerifyCredentialResult } from "../models/components/verifycredentialresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  VerifyGcpIamCredentialRequest,
  VerifyGcpIamCredentialSecurity,
} from "../models/operations/verifygcpiamcredential.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type VerifyGcpIamCredentialMutationVariables = {
  request: VerifyGcpIamCredentialRequest;
  security?: VerifyGcpIamCredentialSecurity | undefined;
  options?: RequestOptions;
};

export type VerifyGcpIamCredentialMutationData = VerifyCredentialResult;

export type VerifyGcpIamCredentialMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * verifyGcpIamCredential externalCredentials
 *
 * @remarks
 * Probe that Speakeasy can impersonate the service account a GCP IAM credential names, and report the principal it resolves to. Ephemeral: nothing is persisted. Rate limited per organization. Requires org:admin.
 */
export function useVerifyGcpIamCredentialMutation(
  options?: MutationHookOptions<
    VerifyGcpIamCredentialMutationData,
    VerifyGcpIamCredentialMutationError,
    VerifyGcpIamCredentialMutationVariables
  >,
): UseMutationResult<
  VerifyGcpIamCredentialMutationData,
  VerifyGcpIamCredentialMutationError,
  VerifyGcpIamCredentialMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildVerifyGcpIamCredentialMutation(client, options),
    ...options,
  });
}

export function mutationKeyVerifyGcpIamCredential(): MutationKey {
  return ["@gram/client", "externalCredentials", "verifyGcpIam"];
}

export function buildVerifyGcpIamCredentialMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: VerifyGcpIamCredentialMutationVariables,
  ) => Promise<VerifyGcpIamCredentialMutationData>;
} {
  return {
    mutationKey: mutationKeyVerifyGcpIamCredential(),
    mutationFn: function verifyGcpIamCredentialMutationFn({
      request,
      security,
      options,
    }): Promise<VerifyGcpIamCredentialMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalCredentialsVerifyGcpIam(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
